import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface InputData {
  rdfAvailable: number;
  biogasIntegration: number;
  clinkerSubstitution: number;
  scrapRatios: {
    steel: number;
    cement: number;
  };
  fossilFuelDisplacement: number;
}

export interface CalculationResults {
  co2Savings: number;
  financialOutcomes: {
    costSavings: number;
    revenue: number;
    netBenefit: number;
  };
  creditPotential: {
    carbonCredits: number;
    itmoValue: number;
  };
}

interface DataContextType {
  inputData: InputData;
  results: CalculationResults | null;
  updateData: (data: InputData, results: CalculationResults) => void;
  hasCustomData: boolean;
  scenarioName: string;
  setScenarioName: (name: string) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [inputData, setInputData] = useState<InputData>({
    rdfAvailable: 0,
    biogasIntegration: 0,
    clinkerSubstitution: 0,
    scrapRatios: { steel: 0, cement: 0 },
    fossilFuelDisplacement: 0
  });

  const [results, setResults] = useState<CalculationResults | null>(null);
  const [hasCustomData, setHasCustomData] = useState(false);
  const [scenarioName, setScenarioName] = useState('Default');

  const updateData = (newData: InputData, newResults: CalculationResults) => {
    setInputData(newData);
    setResults(newResults);
    setHasCustomData(true);
  };

  const contextValue: DataContextType = {
    inputData,
    results,
    updateData,
    hasCustomData,
    scenarioName,
    setScenarioName
  };

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};
